import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { validateName, validatePhone, validateEmail } from '../../utils/validation.js';
import { config } from '../../config/config.js';
import { detectCountry } from '../../utils/country.js';
import Layout from '../Layout/Layout.jsx';
import styles from '../Common/Common.module.css';
import PageTitle from '../Common/PageTitle.jsx';
import './PhoneInput.css';

const FormStep = ({ onSubmit, isLoading }) => {
  // Set default DOB to 20 years ago (reasonable for college students)
  const getDefaultDOB = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 20);
    return date.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: getDefaultDOB(),
    phone: '',
    email: '', // Optional field
    applicantType: 'first-time' // New field for applicant type
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [defaultCountry, setDefaultCountry] = useState('US');

  // Auto-detect country on component mount
  useEffect(() => {
    const autoDetect = async () => {
      try {
        const country = await detectCountry();
        setDefaultCountry(country);
        console.log('Auto-detected country:', country);
      } catch (error) {
        console.error('Failed to auto-detect country:', error);
        // Keep default 'US'
      }
    };

    autoDetect();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  const validateField = (field, value) => {
    let validation;

    switch (field) {
      case 'firstName':
        validation = validateName(value, 'First name');
        break;
      case 'lastName':
        validation = validateName(value, 'Last name');
        break;
      case 'dateOfBirth':
        // Validate date of birth - must be a valid date and not in the future
        if (!value) {
          validation = { isValid: false, error: 'Date of birth is required' };
        } else {
          const date = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const year = date.getFullYear();
          const currentYear = today.getFullYear();
          
          if (isNaN(date.getTime())) {
            validation = { isValid: false, error: 'Please enter a valid date' };
          } else if (year < 1900 || year > currentYear) {
            validation = { isValid: false, error: `Year must be between 1900 and ${currentYear}` };
          } else if (date > today) {
            validation = { isValid: false, error: 'Date cannot be in the future' };
          } else {
            validation = { isValid: true, error: '' };
          }
        }
        break;
      case 'phone':
        // Only validate if user has entered something
        if (value && value.length > 0) {
          validation = validatePhone(value);
        } else {
          validation = { isValid: true, error: '' };
        }
        break;
      case 'email':
        validation = validateEmail(value);
        break;
      default:
        validation = { isValid: true, error: '' };
    }

    if (!validation.isValid) {
      setErrors(prev => ({ ...prev, [field]: validation.error }));
    }

    return validation.isValid;
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate first name
    const firstNameValidation = validateName(formData.firstName, 'First name');
    if (!firstNameValidation.isValid) {
      newErrors.firstName = firstNameValidation.error;
    }

    // Validate last name
    const lastNameValidation = validateName(formData.lastName, 'Last name');
    if (!lastNameValidation.isValid) {
      newErrors.lastName = lastNameValidation.error;
    }

    // Validate date of birth
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const date = new Date(formData.dateOfBirth);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const year = date.getFullYear();
      const currentYear = today.getFullYear();
      
      if (isNaN(date.getTime())) {
        newErrors.dateOfBirth = 'Please enter a valid date';
      } else if (year < 1900 || year > currentYear) {
        newErrors.dateOfBirth = `Year must be between 1900 and ${currentYear}`;
      } else if (date > today) {
        newErrors.dateOfBirth = 'Date cannot be in the future';
      }
    }

    // Validate phone
    const phoneValidation = validatePhone(formData.phone);
    if (!phoneValidation.isValid) {
      newErrors.phone = phoneValidation.error;
    }

    // Validate email (only if provided)
    if (formData.email) {
      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.isValid) {
        newErrors.email = emailValidation.error;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      firstName: true,
      lastName: true,
      dateOfBirth: true,
      phone: true,
      email: true
    });

    // Validate form
    if (!validateForm()) {
      return;
    }

    // Format phone number
    const phoneValidation = validatePhone(formData.phone);
    const submissionData = {
      ...formData,
      phone: phoneValidation.formatted
    };

    onSubmit(submissionData);
  };

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.card}>
        <PageTitle 
          title="Share Your Story" 
          subtitle="Help inspire others by sharing your Folds of Honor video. We'll send you a verification code to get started."
        />

        <form onSubmit={handleSubmit}>
          {/* First Name */}
          <div className={styles['form-group']}>
            <label htmlFor="firstName" className={styles.label}>
              FIRST NAME *
            </label>
            <input
              type="text"
              id="firstName"
              className={`${styles.input} ${touched.firstName && errors.firstName ? styles.error : ''}`}
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              onBlur={() => handleBlur('firstName')}
              placeholder="Johny"
              disabled={isLoading}
              autoComplete="given-name"
            />
            {touched.firstName && errors.firstName && (
              <span className={styles['error-message']}>{errors.firstName}</span>
            )}
          </div>

          {/* Last Name */}
          <div className={styles['form-group']}>
            <label htmlFor="lastName" className={styles.label}>
              LAST NAME *
            </label>
            <input
              type="text"
              id="lastName"
              className={`${styles.input} ${touched.lastName && errors.lastName ? styles.error : ''}`}
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              onBlur={() => handleBlur('lastName')}
              placeholder="Cash"
              disabled={isLoading}
              autoComplete="family-name"
            />
            {touched.lastName && errors.lastName && (
              <span className={styles['error-message']}>{errors.lastName}</span>
            )}
          </div>

          {/* Date of Birth */}
          <div className={styles['form-group']}>
            <label htmlFor="dateOfBirth" className={styles.label}>
              DATE OF BIRTH *
            </label>
            <input
              type="date"
              id="dateOfBirth"
              className={`${styles.input} ${touched.dateOfBirth && errors.dateOfBirth ? styles.error : ''}`}
              value={formData.dateOfBirth}
              onChange={(e) => handleChange('dateOfBirth', e.target.value)}
              onBlur={() => handleBlur('dateOfBirth')}
              disabled={isLoading}
              max={new Date().toISOString().split('T')[0]}
            />
            {touched.dateOfBirth && errors.dateOfBirth && (
              <span className={styles['error-message']}>{errors.dateOfBirth}</span>
            )}
          </div>

          {/* Phone Number */}
          <div className={styles['form-group']}>
            <label htmlFor="phone" className={styles.label}>
              PHONE NUMBER *
            </label>
            <PhoneInput
              id="phone"
              international
              defaultCountry={defaultCountry}
              value={formData.phone}
              onChange={(value) => handleChange('phone', value || '')}
              onBlur={() => handleBlur('phone')}
              disabled={isLoading}
              className={`phone-input ${touched.phone && errors.phone ? 'phone-input-error' : ''}`}
              placeholder="839 - 000-0000"
            />
            <span className={styles['input-hint']}>
              Country auto-detected. Include country code (e.g., +1 for USA)
            </span>
            {touched.phone && errors.phone && (
              <span className={styles['error-message']}>{errors.phone}</span>
            )}
          </div>

          {/* Email (Optional) */}
          <div className={styles['form-group']}>
            <label htmlFor="email" className={styles['label-optional']}>
              Email
            </label>
            <input
              type="email"
              id="email"
              className={`${styles['input-optional']} ${touched.email && errors.email ? styles.error : ''}`}
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              placeholder="your@email.com"
              disabled={isLoading}
              autoComplete="email"
            />
            <span className={styles['input-hint']}>
              Provide your email to receive updates about your submission
            </span>
            {touched.email && errors.email && (
              <span className={styles['error-message']}>{errors.email}</span>
            )}
          </div>

          {/* Applicant Type */}
          <div className={styles['form-group']}>
            <label className={styles.label}>
              APPLICANT TYPE *
            </label>
            <div className={styles['radio-group']}>
              <label className={styles['radio-label']}>
                <input
                  type="radio"
                  name="applicantType"
                  value="first-time"
                  checked={formData.applicantType === 'first-time'}
                  onChange={(e) => handleChange('applicantType', e.target.value)}
                  disabled={isLoading}
                  className={styles['radio-input']}
                />
                <span className={styles['radio-text']}>First-Time Applicant</span>
              </label>
              <label className={styles['radio-label']}>
                <input
                  type="radio"
                  name="applicantType"
                  value="returning"
                  checked={formData.applicantType === 'returning'}
                  onChange={(e) => handleChange('applicantType', e.target.value)}
                  disabled={isLoading}
                  className={styles['radio-input']}
                />
                <span className={styles['radio-text']}>Returning Applicant</span>
              </label>
            </div>
            <span className={styles['input-hint']}>
              First-time applicants are applying for the first time. Returning applicants have received Folds of Honor scholarships before.
            </span>
          </div>

          {/* Required fields note */}
          <p className={styles['required-note']}>
            Fields marked with * are required.
          </p>

          {/* Submit Button */}
          <button
            type="submit"
            className={styles.button}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                Sending Code...
              </>
            ) : (
              <>
                Send Verification Code
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 16 16 12 12 8"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
              </>
            )}
          </button>
        </form>
      </div>
      </div>
    </Layout>
  );
};

export default FormStep;
